import { type ReactNode, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import clsx from 'clsx'

type BottomSheetProps = {
  open: boolean
  onClose: () => void
  title?: ReactNode
  description?: ReactNode
  children: ReactNode
  className?: string
  contentClassName?: string
  bodyClassName?: string
  maxBodyHeight?: string
  closeOnBackdrop?: boolean
  hideCloseButton?: boolean
  zIndexClassName?: string
}

export function BottomSheet({
  open,
  onClose,
  title,
  description,
  children,
  className,
  contentClassName,
  bodyClassName,
  maxBodyHeight = '65vh',
  closeOnBackdrop = true,
  hideCloseButton = false,
  zIndexClassName = 'z-[120]',
}: BottomSheetProps) {
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div
      className={clsx('fixed inset-0 flex flex-col justify-end', zIndexClassName, className)}
      onClick={() => {
        if (closeOnBackdrop) onClose()
      }}
    >
      <div className="absolute inset-0 bg-black/30 animate-fade-in" />
      <div
        className={clsx(
          'relative glass-dropdown rounded-t-3xl glass-appear',
          'pb-[calc(16px+var(--tg-safe-area-inset-bottom,env(safe-area-inset-bottom,0px)))]',
          contentClassName,
        )}
        style={{ paddingTop: '12px', paddingLeft: '16px', paddingRight: '16px' }}
        onClick={(event) => event.stopPropagation()}
      >
        {(title || description || !hideCloseButton) && (
          <div className="flex items-start justify-between gap-2 pb-2">
            <div className="min-w-0">
              {title && <div className="text-sm font-semibold">{title}</div>}
              {description && (
                <p className="text-[11px] text-telegram-hint mt-1">{description}</p>
              )}
            </div>
            {!hideCloseButton && (
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 rounded-full flex items-center justify-center"
                style={{ width: '26px', height: '26px', background: 'var(--glass-border)', color: 'var(--dropdown-text-color)' }}
              >
                <X size={13} />
              </button>
            )}
          </div>
        )}

        <div className={clsx('overflow-y-auto', bodyClassName)} style={{ maxHeight: maxBodyHeight }}>
          {children}
        </div>
      </div>
    </div>,
    document.body,
  )
}
